import { OAuth2AuthCodePKCE, AccessContext as libAccessContext } from '@bity/oauth2-auth-code-pkce'
import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport'
import {
    UnaryCall,
    RpcError,
    NextUnaryFn,
    MethodInfo,
    RpcOptions,
    NextServerStreamingFn,
    ServerStreamingCall,
    RpcOutputStreamController,
} from '@protobuf-ts/runtime-rpc'
import { NextRouter } from 'next/router'
import { MutedDotsCacheKey } from './constants/system'

export const loginPath = '/login'
export const nextPathLocalStorageKey = 'c1-login-next-path'
const requestSourceHeader = 'c1-request-source'

export const handleNotAuthed = (router: NextRouter): void => {
    if (router.pathname === loginPath) {
        return
    }
    sessionStorage.setItem(nextPathLocalStorageKey, `${window.location.pathname}${window.location.search}`)
    router.push(loginPath)
}

export const handleLogin = (oauth: OAuth2AuthCodePKCE | null | undefined): void => {
    if (!oauth) {
        return
    }
    if (sessionStorage.getItem(nextPathLocalStorageKey) === null) {
        const path = window.location.pathname
        let nextPage = `${window.location.pathname}${window.location.search}`
        if (path === loginPath && window.location.search !== '') {
            // The callback component is the same as the login component
            // This workaround will make sure that we land on the dashboard once we login
            // and not on the login component with just a `hang tight` message
            nextPage = '/'
        }
        sessionStorage.setItem(nextPathLocalStorageKey, nextPage)
    }
    sessionStorage.removeItem(MutedDotsCacheKey)
    oauth.reset()
    oauth.fetchAuthorizationCode()
}

export const handleLogout = async (oauth: OAuth2AuthCodePKCE | undefined): Promise<void> => {
    sessionStorage.removeItem(nextPathLocalStorageKey)
    const body = new FormData()

    // we only want to call "getAccessToken" if we already have a valid token
    // otherwise we're creating work for ourselves by refreshing the token.
    if (oauth && oauth.isAuthorized() && !oauth.isAccessTokenExpired()) {
        try {
            const accessContext = await oauth.getAccessToken()
            if (accessContext.token) {
                body.append('access_token', accessContext.token.value)
            }
        } catch (err) {
            // an error here is fine, we just logout via cookie only
        }
    }

    try {
        await fetch('/auth/v1/logout', {
            method: 'POST',
            body,
        })
    } catch (err) {
      // logout is best effort, and logs failures within auth service.
    }
    if (oauth) {
        oauth.reset()
    }
    window.location.href = '/'
}

export type AccessContext = libAccessContext
export type onAccessTokenExpiryFunc = (refreshAccessToken: () => Promise<AccessContext>) => Promise<AccessContext>
export type onInvalidGrantFunc = (refreshAuthCodeOrRefreshToken: () => Promise<void>) => void

export const getOauthConfig = (
    onAccessTokenExpiry: onAccessTokenExpiryFunc,
    onInvalidGrant: onInvalidGrantFunc,
): OAuth2AuthCodePKCE =>
    new OAuth2AuthCodePKCE({
        authorizationUrl: `${window.location.origin}/auth/v1/authorize`,
        clientId: '1lZ1WQExZzGszDQns7YVp0H23h5',
        tokenUrl: `${window.location.origin}/auth/v1/token`,
        redirectUrl: `${window.location.origin}/login`,
        scopes: [''],
        onAccessTokenExpiry,
        onInvalidGrant,
    })

export const createTransport = (
    tokenSource: OAuth2AuthCodePKCE,
    onError: (arg0: Error) => void,
): GrpcWebFetchTransport => {
    let pendingFetch: Promise<string> | null = null
    const doFetchToken = async (): Promise<string> => {
        let token = ''
        try {
            const ac = await tokenSource.getAccessToken()
            token = ac.token?.value || ''
        } catch (err) {
            if (err) {
                onError(new Error(JSON.stringify(err)))
            } else {
                onError(new Error('OAuthAuthInterceptor: fetchToken() error'))
            }
        }

        if (token === '') {
            onError(new Error('OAuthAuthInterceptor: fetchToken() empty token'))
        }
        pendingFetch = null
        return token
    }
    const fetchToken = async (): Promise<string> => {
        if (pendingFetch === null) {
            pendingFetch = doFetchToken()
        }
        return pendingFetch
    }

    return new GrpcWebFetchTransport({
        baseUrl: window.origin,
        fetchInit: { credentials: 'include' },
        // see https://github.com/timostamm/protobuf-ts/blob/master/packages/runtime-rpc/src/rpc-interceptor.ts
        interceptors: [
            {
                interceptUnary: (
                    next: NextUnaryFn,
                    method: MethodInfo,
                    input: object,
                    options: RpcOptions,
                ): UnaryCall => {
                    if (!options.meta) {
                        options.meta = {}
                    }
                    // https://github.com/timostamm/protobuf-ts/issues/31

                    // next() creates a call instance, sending data (including headers) to the server.
                    // we want to wait until we have our token:
                    //
                    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                    const callPromise = new Promise<UnaryCall<any, any>>((resolve) => {
                        fetchToken().then((token) => {
                            if (!options.meta) {
                                options.meta = {}
                            }
                            options.meta.Authorization = `Bearer ${token}`
                            options.meta[requestSourceHeader] = window.location.href
                            resolve(next(method, input, options))
                        })
                    })

                    // we have to return a valid call instance immediately.
                    // we wrap the promised call.
                    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                    const call = new UnaryCall<any, any>(
                        method,
                        options.meta,
                        input,
                        callPromise.then((c) => c.headers),
                        callPromise.then((c) => c.response),
                        callPromise.then((c) => c.status),
                        callPromise.then((c) => c.trailers),
                    )

                    call.status.catch((err: RpcError) => {
                        // use GrpcStatusCode.UNAUTHENTICATED to trigger session error
                        // GrpcStatusCode.UNAUTHENTICATED... as a string? ugh.
                        if (err.code === 'UNAUTHENTICATED') {
                            onError(err)
                        }
                    })

                    return call
                },
                // https://github.com/timostamm/protobuf-ts/blob/16ce40d32df2f847692543c83cff13bc80d6eece/packages/example-angular-app/src/app/interceptor-examples/promise-request-headers.ts
                interceptServerStreaming: (
                    next: NextServerStreamingFn,
                    method: MethodInfo,
                    input: object,
                    options: RpcOptions,
                ): ServerStreamingCall => {
                    if (!options.meta) {
                        options.meta = {}
                    }
                    const outputStream = new RpcOutputStreamController()
                    const callPromise = new Promise<ServerStreamingCall>((resolve) => {
                        fetchToken().then((token) => {
                            if (!options.meta) {
                                options.meta = {}
                            }
                            options.meta.Authorization = `Bearer ${token}`

                            const nextCall = next(method, input, options)
                            resolve(nextCall)

                            nextCall.responses.onNext(outputStream.notifyNext.bind(outputStream))
                        })
                    })

                    const rv = new ServerStreamingCall(
                        method,
                        options.meta ?? {},
                        input,
                        callPromise.then((c) => c.headers),
                        outputStream,
                        callPromise.then((c) => c.status),
                        callPromise.then((c) => c.trailers),
                    )

                    rv.status.catch((err: RpcError) => {
                        // use GrpcStatusCode.UNAUTHENTICATED to trigger session error
                        // GrpcStatusCode.UNAUTHENTICATED... as a string? ugh.
                        if (err.code === 'UNAUTHENTICATED') {
                            onError(err)
                        }
                    })
                    return rv
                },
            },
        ],
    })
}
